import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import { ArchiveIcon, ArrowLeftIcon, UndoIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Goal as GoalEntity,
	GoalFeasibility,
	GoalOwnerType,
	User,
	useArchiveGoalMutation,
	useIndividualGoalQuery,
	useRestoreGoalMutation,
	useUpdateGoalMutation,
} from '@treams/treams-network';
import {
	Author,
	Button,
	CalendarIcon,
	Callout,
	CompassIcon,
	Container,
	FlexGap,
	Heading3,
	IconButton,
	PAGE_CONTENT_WIDTH,
	Paragraph,
	PencilEditBoxIcon,
	Pill,
	PropertyChip,
	QuillText,
	Spinner,
	TargetIcon,
	Title,
	Toast,
	Todo,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import Comments from '../comments/Comments';
import { ModalContext } from '../navigation/ModalNavigation';
import GoalAuditTrail from './GoalAuditTrail';
import { GoodHabitzPill } from './GoodHabitzLogo';
import Subgoals, { SubgoalsContext } from './subgoals/Subgoals';
import SubgoalsContainer from './subgoals/SubgoalsContainer';
import { goodHabitzCourseCount } from './util/goodHabitzCourseCount';

export function FeasibilityTag(props: {
	feasibility: GoalFeasibility;
	label?: string;
	plain?: boolean;
}) {
	const { t } = useTranslation();

	const feasibilityMap = {
		[GoalFeasibility.CloseCall]: {
			label: t('goals.show.feasibility.closeCall'),
			tint: 'warning',
		},
		[GoalFeasibility.WillMakeIt]: {
			label: t('goals.show.feasibility.willMakeIt'),
			tint: 'success',
		},
		[GoalFeasibility.WontMakeIt]: {
			label: t('goals.show.feasibility.wontMakeIt'),
			tint: 'error',
		},
	};

	const { feasibility } = props;
	const { label, tint } = feasibilityMap[feasibility];

	return (
		<Pill tint={tint} small={props.plain}>
			{props.label ? `${props.label} ${label}` : label}
		</Pill>
	);
}

const Strikethrough = styled.span`
	text-decoration: line-through;
	color: ${tokens.text.secondary};
`;

const TITLE_TODO_SIZE = 32;

function useGoalAuditTrailWorkspacePermissions(workspaceID?: string): boolean {
	const { data } = graphql.useGoalAuditTrailWorkspaceQuery({
		variables: { workspaceID: workspaceID! },
		skip: !workspaceID,
	});
	return data?.workspace.viewerCanViewGoalAuditTrail ?? false;
}

function useGoalAuditTrailGroupPermissions(groupID?: string): boolean {
	const { data } = graphql.useGoalAuditTrailGroupQuery({
		variables: { groupID: groupID! },
		skip: !groupID,
	});
	return data?.group.viewerCanViewGoalAuditTrail ?? false;
}

function GoalAuditTrailContainer(props: { goalID: string; goal?: GoalEntity }) {
	const hasAudit = useFeature(graphql.FeatureName.GoalsAudit);
	const workspacePermissions = useGoalAuditTrailWorkspacePermissions(
		props.goal?.ownerType === GoalOwnerType.Workspace ? props.goal?.owner?.id : undefined,
	);
	const groupPermissions = useGoalAuditTrailGroupPermissions(
		props.goal?.ownerType === GoalOwnerType.Department ||
			props.goal?.ownerType === GoalOwnerType.Team
			? props.goal?.owner?.id
			: undefined,
	);

	const { t } = useTranslation();

	if (!hasAudit) {
		return null;
	}

	if (
		props.goal?.owner &&
		(props.goal.ownerType === GoalOwnerType.Department ||
			props.goal.ownerType === GoalOwnerType.Team ||
			props.goal.ownerType === GoalOwnerType.Workspace)
	) {
		if (!workspacePermissions && !groupPermissions) {
			return null;
		}
	}

	return (
		<Container py={`${tokens.gap.section}px`} wide={PAGE_CONTENT_WIDTH}>
			<Heading3 mb={`${tokens.gap.list}px`}>{t('goals.show.audit.heading')}</Heading3>
			<GoalAuditTrail goalID={props.goalID} />
		</Container>
	);
}

export default function Goal(props: { userID?: string; goalID: string }) {
	const comments = useRef<HTMLDivElement>(null);

	const { data, error, refetch } = useIndividualGoalQuery({
		variables: { id: props.goalID },
	});

	const [archive, { loading: isLoadingArchive }] = useArchiveGoalMutation();
	const [restore, { loading: isLoadingRestore }] = useRestoreGoalMutation();
	const [update, { loading: isLoadingUpdate }] = useUpdateGoalMutation();

	const { localize } = useLocalize();
	const history = useHistory();
	const location = useLocation();
	const { t } = useTranslation();
	const { addToast } = useToasts();

	const isArchived = !!data?.goal.isArchived;

	const onArchiveRestore = async () => {
		if (isArchived) {
			await restore({ variables: { input: { id: props.goalID } } });
			return;
		}
		await archive({ variables: { input: { id: props.goalID } } });
	};

	const returnUrl = (() => {
		const params = new URLSearchParams(history.location.search);
		if (params.has('return') && params.get('return')?.startsWith('/')) {
			return params.get('return')!;
		}

		if (location.state?.returnURL) {
			return location.state.returnURL;
		}

		if (
			data?.goal.ownerType === GoalOwnerType.Department ||
			data?.goal.ownerType === GoalOwnerType.Team
		) {
			return '/goals/collective';
		}

		if (data?.goal.ownerType === GoalOwnerType.Workspace) {
			return '/goals/organization';
		}

		if (props.userID) {
			return `/users/${props.userID}/goals`;
		}

		return '/goals';
	})();

	const handleProgressUpdate = async () => {
		if (data) {
			const { goal } = data;

			let progressOverride: number | undefined = 1.0;

			if (goal.progress === 1.0) {
				progressOverride = undefined;
			}

			const {
				id,
				title,
				description,
				checklistItems,
				dueDate,
				feasibility,
				linkedGoalDrivers,
				competency,
			} = goal;

			await update({
				variables: {
					input: {
						id: id,
						title: title,
						description: description,
						checklistItems: checklistItems.map((item) => ({
							id: item.id,
							description: item.description,
							progress:
								item.progress.lower != null
									? {
											type: item.progress.type,
											value: item.progress.value,
											lower: item.progress.lower,
											upper: item.progress.upper,
										}
									: {
											type: item.progress.type,
											value: item.progress.value,
										},
							title: item.title,
							dueDate: item.dueDate,
							learningCourseID: item.learningCourseID,
						})),
						dueDate: dueDate,
						feasibility: feasibility ?? undefined,
						linkedGoalDriverIDs: linkedGoalDrivers.map((driver) => driver.id),
						linkedGoalIDs: [],
						competency: competency
							? {
									competencyID: competency.competency.id,
									competencyLevelIndex: competency.competencyLevelIndex,
								}
							: undefined,
						labelID: goal.label?.id,
						progressOverride,
					},
				},
			});

			await refetch();
		}
	};

	const progress = data?.goal.progress === undefined ? 0 : data.goal.progress;
	const goodHabitzCount = data ? goodHabitzCourseCount(data.goal) : 0;

	const checkState = progress === 1.0 ? true : progress > 0.0 && progress < 1.0 ? null : false;

	const [showCompletionToast, setShowCompletionToast] = useState(false);

	useEffect(() => {
		if (checkState && !isArchived && showCompletionToast) {
			addToast(
				<Toast
					appearance="success"
					title={t('goals.show.completionToast.title')}
					description={t('goals.show.completionToast.description')}
					action={
						<Button
							title={t('goals.show.toolbar.archive')}
							type="destructive"
							onClick={onArchiveRestore}
						/>
					}
				/>,
				{
					appearance: 'success',
					autoDismiss: true,
					autoDismissTimeout: 5000,
				},
			);

			setShowCompletionToast(false);
		} else if (data) {
			setShowCompletionToast(true);
		}
	}, [checkState, data?.goal.id]);

	if (error) {
		return (
			<Container py={4} wide={PAGE_CONTENT_WIDTH}>
				<Callout
					color="red"
					title={t('goals.show.error.title')}
					description={t('goals.show.error.description')}
				/>
			</Container>
		);
	}

	return (
		<>
			<Container py={4} wide={PAGE_CONTENT_WIDTH}>
				<Flex justifyContent="space-between" alignItems="center" mb={`${tokens.gap.section}px`}>
					<ModalContext.Consumer>
						{(value) =>
							!value.isModal && (
								<Button
									icon={<ArrowLeftIcon />}
									link={returnUrl}
									title={t('goals.show.toolbar.back')}
									type="plain"
									ml="-16px"
								/>
							)
						}
					</ModalContext.Consumer>
					{data?.goal.viewerCanUpdate && (
						<FlexGap gap={tokens.gap.list} alignItems="center">
							{isArchived ? (
								<Button
									type="secondary"
									icon={<UndoIcon />}
									title={t('goals.show.toolbar.restore')}
									onClick={() => {
										onArchiveRestore();
									}}
									loading={isLoadingRestore}
									disabled={isLoadingUpdate}
								/>
							) : (
								<IconButton
									type="secondary"
									icon={<ArchiveIcon />}
									onClick={() => {
										onArchiveRestore();
									}}
									loading={isLoadingArchive}
									disabled={isLoadingUpdate}
								/>
							)}
							<Button
								type="secondary"
								title={t('goals.show.toolbar.edit')}
								icon={<PencilEditBoxIcon />}
								link={
									props.userID
										? `/users/${props.userID}/goals/${props.goalID}/edit`
										: `/goals/${props.goalID}/edit`
								}
							/>
						</FlexGap>
					)}
				</Flex>

				{isArchived && (
					<Callout
						mb={`${tokens.gap.list}px`}
						color="blue"
						icon={<ArchiveIcon />}
						title={t('goals.show.archived.title')}
						description={t('goals.show.archived.description')}
					/>
				)}

				{data ? (
					<Box>
						<FlexGap alignItems="center" mb={`${tokens.gap.list}px`} gap={tokens.gap.element}>
							<Box flexGrow={0} flexShrink={0}>
								{isLoadingUpdate ? (
									<Spinner
										width={TITLE_TODO_SIZE}
										height={TITLE_TODO_SIZE}
										color={tokens.border.subtle}
									/>
								) : (
									<Todo
										checked={checkState}
										size={TITLE_TODO_SIZE}
										onChange={
											isLoadingArchive || isLoadingRestore
												? undefined
												: () => handleProgressUpdate()
										}
									/>
								)}
							</Box>
							<Title>
								{data.goal!.completedAt ? (
									<Strikethrough>{localize(data.goal!.title).text}</Strikethrough>
								) : (
									localize(data.goal!.title).text
								)}
							</Title>
						</FlexGap>
						<Box mb={`${tokens.gap.list}px`}>
							<QuillText value={data.goal!.description} />
						</Box>
						<FlexGap mb={`${tokens.gap.list}px`} gap={tokens.gap.element} flexWrap="wrap">
							{data.goal?.dueDate && (
								<PropertyChip
									icon={<CalendarIcon />}
									label={moment(data.goal.dueDate).format('LL')}
								/>
							)}
							{data.goal.parent && (
								<PropertyChip
									icon={<TargetIcon />}
									label={localize(data.goal.parent.title).text}
									onClick={() => history.push(`/goals/${data.goal.parent!.id}`)}
								/>
							)}
							{data.goal?.feasibility && <FeasibilityTag feasibility={data.goal.feasibility} />}
							{data.goal?.competency && (
								<PropertyChip
									icon={<CompassIcon />}
									label={localize(data.goal.competency.competency.title).text}
								/>
							)}
							{data.goal?.label && <Pill>{localize(data.goal.label.title).text}</Pill>}
							{goodHabitzCount > 0 && <GoodHabitzPill count={goodHabitzCount} />}
						</FlexGap>
						<Author
							description={t('goals.show.author.createdAt')}
							user={data.goal!.createdBy as User}
							createdAt={data.goal!.createdAt}
						/>
					</Box>
				) : (
					<Box>
						<Title.Skeleton mb={`${tokens.gap.list}px`} />
						<Paragraph.Skeleton mb={`${tokens.gap.list}px`} />
						<Author.Skeleton />
					</Box>
				)}
			</Container>
			<SubgoalsContainer>
				{data ? (
					<SubgoalsContext
						key={data.goal.id}
						goal={data.goal as GoalEntity}
						editable={data.goal.viewerCanUpdate}
					/>
				) : (
					<Subgoals.Skeleton />
				)}
			</SubgoalsContainer>

			{data?.goal.viewerCanViewComments !== false && (
				<Container
					{...({ ref: comments } as { ref: React.RefObject<HTMLDivElement> })}
					py={`${tokens.gap.list}px`}
					wide={PAGE_CONTENT_WIDTH}
				>
					<Heading3 mb={`${tokens.gap.list}px`}>{t('goals.show.comments.heading')}</Heading3>
					{data ? <Comments target={data.goal} /> : <Comments.Skeleton />}
				</Container>
			)}

			<GoalAuditTrailContainer goalID={props.goalID} goal={data?.goal as GoalEntity} />
		</>
	);
}
